import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useMatch } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';

import PageLoaderContainer from 'components/containers/PageLoaderContainer';

import useAccessToken from 'hooks/useAccessToken';
import useAccount from 'hooks/useAccount';
import useCurrentOrganization from 'hooks/useCurrentOrganization';
import useLogout from 'hooks/useLogout';
import useNavigation from 'hooks/useNavigation';
import useOrganizations from 'hooks/useOrganizations';
import usePageRoutes from 'hooks/usePageRoutes';

import useCreateOrganization from './hooks/useCreateOrganization';
import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { FunctionComponent, Organization, Props } from './types';

const AuthChecker: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const { ORGANIZATION } = useTranslation();

    const { navigate } = useNavigation();
    const { isAuthenticated, isLoading } = useAuth0();
    const { isLoggingOut } = useLogout();
    const { createNewOrganization } = useCreateOrganization();

    const {
        fetchOrganizations,
        isLoading: isLoadingOrganizations,
        organizations
    } = useOrganizations();

    const { fetchToken } = useAccessToken();

    const { currentOrganization, setCurrentOrganization } = useCurrentOrganization();

    const { isAccountComplete, isEmailVerified, resolveUserData, user } = useAccount();

    const {
        accountLoginPage,
        homePage,
        verifyAccountPage,
        verifyEmailPage,
        verifyOrganizationPage
    } = usePageRoutes();

    const loginMatch = useMatch(accountLoginPage);
    const onboardingMatch = useMatch('/verify/*');
    const orgMatch = useMatch('/verify/organization/*');

    const loginToOrganization = useCallback(
        async (org: Organization) => {
            const token = await fetchToken(org.auth0Id, false);

            if (!token) {
                return;
            }

            await setCurrentOrganization(org);
        },
        [fetchToken, setCurrentOrganization]
    );

    const [isLoadingUserData, setLoadingUserData] = useState(false);

    useEffect(() => {
        if (isAuthenticated && !isLoading && !isLoadingUserData && !user) {
            setLoadingUserData(true);
            resolveUserData();
        }
    }, [isAuthenticated, isLoading, isLoadingUserData, resolveUserData, user]);

    useEffect(() => {
        if (
            isLoading ||
            organizations ||
            !isAuthenticated ||
            !isEmailVerified ||
            !isAccountComplete
        ) {
            return;
        }

        const createOrganization = async () => {
            if ((user && !organizations) || organizations?.length < 1) {
                const companyName = `${user?.firstName}'s ${ORGANIZATION}`;

                const newOrganization = await createNewOrganization({ companyName });

                if (newOrganization) {
                    await loginToOrganization(newOrganization);
                }
            }
        };

        const fetch = async () => {
            const { data, error } = await fetchOrganizations();

            if (error) {
                return;
            }

            const organizationMap = [
                async () => await createOrganization(),
                async () => await loginToOrganization(data[0])
            ];

            if (organizationMap[data?.length]) {
                await organizationMap[data?.length]();
            } else {
                navigate(verifyOrganizationPage);
            }
        };

        if (!isLoadingOrganizations && !currentOrganization) {
            fetch();
        }

        return;
    }, [
        ORGANIZATION,
        createNewOrganization,
        currentOrganization,
        fetchOrganizations,
        isAccountComplete,
        isAuthenticated,
        isEmailVerified,
        isLoading,
        isLoadingOrganizations,
        loginToOrganization,
        navigate,
        organizations,
        orgMatch,
        user,
        verifyOrganizationPage
    ]);

    if (isLoading || isLoadingOrganizations) {
        return <PageLoaderContainer testId={testId} />;
    }

    if (isLoggingOut || !isAuthenticated) {
        return <Navigate data-testid={testId} replace to={accountLoginPage} />;
    }

    if (!user) {
        return <PageLoaderContainer testId={testId} />;
    }

    if (!isEmailVerified) {
        if (!onboardingMatch) {
            return <Navigate data-testid={testId} replace to={verifyEmailPage} />;
        } else if (!orgMatch) {
            return <Outlet data-testid={testId} />;
        }

        return null;
    }

    if (!isAccountComplete && user) {
        if (!onboardingMatch) {
            return <Navigate data-testid={testId} replace to={verifyAccountPage} />;
        }

        return <Outlet data-testid={testId} />;
    }

    if (organizations && orgMatch) {
        if (currentOrganization) {
            return <Navigate data-testid={testId} replace to={homePage} />;
        }

        return <Outlet data-testid={testId} />;
    }

    if (organizations) {
        if (currentOrganization && onboardingMatch) {
            return <Navigate data-testid={testId} replace to={homePage} />;
        }
    }

    if ((user && currentOrganization) || loginMatch) {
        return <Outlet data-testid={testId} />;
    }

    return null;
};

export default AuthChecker;
